'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'
import { TextPlugin } from 'gsap/TextPlugin'
import { CustomEase } from 'gsap/CustomEase'
import { CustomBounce } from 'gsap/CustomBounce'

gsap.registerPlugin(useGSAP, DrawSVGPlugin, MotionPathPlugin, TextPlugin, CustomEase, CustomBounce)

const LOADER_PRIMARY = 'hsl(var(--primary))'

// Enhance the Window interface for GSAP plugins
declare global {
  interface Window {
    DrawSVGPlugin?: typeof DrawSVGPlugin
    MotionPathPlugin?: typeof MotionPathPlugin
    TextPlugin?: typeof TextPlugin
    CustomEase?: typeof CustomEase
    CustomBounce?: typeof CustomBounce
  }
}

interface BrandedLoaderProps {
  onComplete?: () => void
  className?: string
  message?: string
}

export function BrandedLoader({ onComplete, className = '', message }: BrandedLoaderProps) {
  const [progress, setProgress] = useState(0)
  const [loadingText, setLoadingText] = useState(message || 'Loading NPFC...')
  const [isHydrated, setIsHydrated] = useState(false)
  const loaderRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<SVGSVGElement>(null)
  const progressBarRef = useRef<HTMLDivElement>(null)
  const progressFillRef = useRef<HTMLDivElement>(null)
  const completedRef = useRef(false)

  // Hydration check to prevent SSR/client mismatch
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Production-ready completion handler that prevents duplicate calls
  const handleComplete = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true

    if (onComplete) {
      setTimeout(() => onComplete(), 500)
    }
  }, [onComplete])

  // Mandatory timeout failsafe - NEVER let user get stuck
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!completedRef.current) {
        console.warn('BrandedLoader: Timeout reached, forcing completion')
        handleComplete()
      }
    }, 14000) // 18 second maximum for sophisticated drawing

    return () => clearTimeout(timeoutId)
  }, [handleComplete])

  // Award-winning GSAP animation with sophisticated DrawSVG implementation
  useGSAP(
    () => {
      if (!isHydrated || !loaderRef.current || !logoRef.current) {
        if (!isHydrated) {
          // Don't warn during initial hydration
          return
        }
        console.warn('BrandedLoader: Required refs not available, using fallback')
        handleComplete()
        return
      }

      try {
        // Force register DrawSVG plugin for premium animations
        if (typeof window !== 'undefined') {
          window.DrawSVGPlugin = DrawSVGPlugin
          window.MotionPathPlugin = MotionPathPlugin
          window.TextPlugin = TextPlugin
          window.CustomEase = CustomEase
          window.CustomBounce = CustomBounce
        }

        // Validate plugin availability with forced registration
        const hasDrawSVG = !!(
          DrawSVGPlugin ||
          (gsap.plugins as any)?.DrawSVGPlugin ||
          window.DrawSVGPlugin
        )
        const hasMotionPath = !!(
          MotionPathPlugin ||
          (gsap.plugins as any)?.MotionPathPlugin ||
          window.MotionPathPlugin
        )
        const hasTextPlugin = !!(
          TextPlugin ||
          (gsap.plugins as any)?.TextPlugin ||
          window.TextPlugin
        )
        const hasCustomEase = !!(
          CustomEase ||
          (gsap.plugins as any)?.CustomEase ||
          window.CustomEase
        )
        const hasCustomBounce = !!(
          CustomBounce ||
          (gsap.plugins as any)?.CustomBounce ||
          window.CustomBounce
        )

        // Create award-winning custom eases for premium animations
        let morphEase = 'power2.out'
        let logoRevealEase = 'back.out(1.7)'
        let drawingEase = 'power1.inOut'
        let particleEase = 'elastic.out(1, 0.5)'

        if (hasCustomEase && hasCustomBounce) {
          try {
            CustomEase.create(
              'morphEase',
              'M0,0 C0.126,0.382 0.282,0.674 0.44,0.822 0.632,1.002 0.818,1.001 1,1'
            )
            CustomEase.create('drawingEase', 'M0,0 C0.215,0.61 0.355,1 1,1')
            CustomEase.create('particleEase', 'M0,0 C0.68,-0.55 0.265,1.55 1,1')
            CustomBounce.create('logoReveal', { strength: 0.7, endAtStart: false, squash: 3 })
            morphEase = 'morphEase'
            logoRevealEase = 'logoReveal'
            drawingEase = 'drawingEase'
            particleEase = 'particleEase'
          } catch (error) {
            console.warn('BrandedLoader: Custom ease creation failed, using fallbacks', error)
          }
        }

        // Validate DOM elements for sophisticated animation
        const elements = {
          loaderContent: document.querySelector('.loader-content'),
          backgroundElements: document.querySelector('.background-elements'),
          strokeDrawingLayer: document.querySelector('.stroke-drawing-layer'),
          particleSystem: document.querySelector('.particle-system'),
          textElements: document.querySelector('.text-elements'),
          letterF: document.querySelector('.letter-f'),
          letterA1: document.querySelector('.letter-a1'),
          letterN: document.querySelector('.letter-n'),
          letterT: document.querySelector('.letter-t'),
          letterA2: document.querySelector('.letter-a2'),
          letterS: document.querySelector('.letter-s'),
          letterY: document.querySelector('.letter-y'),
          space: document.querySelector('.letter-space'),
          letterX: document.querySelector('.letter-x'),
          letterI: document.querySelector('.letter-i'),
          loadingText: document.querySelector('.loading-text'),
          logoFills: document.querySelector('.logo-fills')
        }

        const missingElements = Object.entries(elements)
          .filter(([key, el]) => !el || (el instanceof NodeList && el.length === 0))
          .map(([key]) => key)

        if (missingElements.length > 0) {
          console.warn('BrandedLoader: Missing DOM elements:', missingElements)
        }

        // ========================
        // AWARD-WINNING ANIMATION TIMELINE SETUP
        // ========================

        const masterTimeline = gsap.timeline({
          onComplete: handleComplete,
          onError: (error: any) => {
            console.error('BrandedLoader: Timeline error:', error)
            handleComplete()
          }
        })

        // ========================
        // SOPHISTICATED INITIAL SETUP
        // ========================

        // IMMEDIATE SETUP - Prevent any glimpse of unstyled content
        // Set all elements to hidden state IMMEDIATELY
        gsap.set('.loader-content', {
          opacity: 0,
          y: 50,
          rotationX: -15,
          scale: 0.95,
          transformOrigin: 'center center',
          transformPerspective: 1000
        })

        // Progress system setup
        gsap.set('.progress-glow', { opacity: 0, scale: 0.8 })
        gsap.set('.loading-text', { opacity: 0, y: 20 })

        // Rich background elements with sophisticated positioning
        gsap.set('.background-elements', {
          scale: 0.85,
          opacity: 0,
          rotation: 0.5,
          transformOrigin: 'center center'
        })

        // Stroke drawing layer with premium effects
        gsap.set('.stroke-drawing-layer', {
          opacity: 0,
          scale: 1.05,
          filter: 'blur(1px)'
        })

        // ========================
        // PREMIUM DrawSVG SETUP WITH FALLBACKS
        // ========================

        const strokePaths = document.querySelectorAll('.stroke-path')
        const textPaths = document.querySelectorAll('.text-path')

        if (hasDrawSVG && strokePaths.length > 0) {
          strokePaths.forEach((path, index) => {
            try {
              // Premium DrawSVG initialization
              gsap.set(path, {
                drawSVG: '0%',
                opacity: 0,
                strokeWidth: '2',
                filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))'
              })
            } catch (error) {
              console.warn(`BrandedLoader: DrawSVG setup failed for path ${index}:`, error)
              // Fallback to traditional stroke animation
              gsap.set(path, {
                strokeDasharray: '2000',
                strokeDashoffset: '2000',
                opacity: 0
              })
            }
          })
        } else {
          strokePaths.forEach((path) => {
            gsap.set(path, {
              strokeDasharray: '2000',
              strokeDashoffset: '2000',
              opacity: 0,
              filter: 'drop-shadow(0 0 3px rgba(59, 130, 246, 0.5))'
            })
          })
        }

        // Premium particle system setup - Enhanced visibility
        gsap.set('.particle-system', { opacity: 1 })
        const particles = document.querySelectorAll('.particle')
        particles.forEach((particle, index) => {
          gsap.set(particle, {
            scale: 0,
            opacity: 0,
            rotation: 0,
            x: 0,
            y: 0,
            transformOrigin: 'center center'
          })
        })

        // Sophisticated text elements setup
        textPaths.forEach((textPath) => {
          gsap.set(textPath, {
            opacity: 0,
            scale: 0.9,
            transformOrigin: 'center center'
          })
        })

        // Logo fills with premium glow effects
        gsap.set('.logo-fills', {
          opacity: 0,
          scale: 1.02,
          filter: 'brightness(1.1) saturate(1.1)'
        })

        // ========================
        // AWARD-WINNING MAIN ANIMATION SEQUENCE
        // ========================

        masterTimeline
          // ===== PHASE 1: SPECTACULAR ENTRANCE (0-10%) - 2X FASTER =====
          .to('.loader-content', {
            opacity: 1,
            y: 0,
            rotationX: 0,
            scale: 1,
            duration: 0.9,
            ease: logoRevealEase,
            onUpdate: function () {
              const progressVal = Math.round(this.progress() * 10)
              setProgress(progressVal)
            }
          })

          // ===== PHASE 2: BACKGROUND EMERGENCE (10-20%) - 2X FASTER =====
          .to(
            '.background-elements',
            {
              opacity: 0.5,
              scale: 1,
              rotation: 0,
              duration: 1.1,
              ease: morphEase,
              onUpdate: function () {
                const progressVal = Math.round(10 + this.progress() * 10)
                setProgress(progressVal)
              }
            },
            '-=0.5'
          )

          // ===== PHASE 3: STROKE LAYER ACTIVATION (15-25%) - 2X FASTER =====
          .to(
            '.stroke-drawing-layer',
            {
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
              duration: 0.25,
              ease: 'power2.out',
              onUpdate: function () {
                const progressVal = Math.round(15 + this.progress() * 10)
                setProgress(progressVal)
              }
            },
            '-=0.6'
          )

          // ===== PHASE 4: PARTICLE SYSTEM ACTIVATION (20-30%) - 2X FASTER =====
          .to(
            '.particle-system',
            {
              opacity: 1,
              duration: 0.15,
              ease: 'power2.out',
              onUpdate: function () {
                const progressVal = Math.round(20 + this.progress() * 10)
                setProgress(progressVal)
              }
            },
            '-=0.2'
          )

          // ===== PHASE 5: AWARD-WINNING Fantasy XI DRAWING SEQUENCE (30-85%) =====
          .add(() => {
            setProgress(30)
          })

          // === F LETTER DRAWING - ARCHITECTURAL PRECISION ===
          .add(() => {
            const fStrokes = ['stroke-f-1', 'stroke-f-2', 'stroke-f-3']
            const drawLetter = (
              strokeClass: string,
              delay: number,
              duration: number,
              progressStart: number
            ) => {
              const element = document.querySelector(`.${strokeClass}`)
              if (element) {
                if (hasDrawSVG) {
                  try {
                    gsap.to(element, {
                      drawSVG: '100%',
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: drawingEase,
                      onUpdate: function () {
                        setProgress(Math.round(progressStart + this.progress() * 8))
                      }
                    })
                  } catch (error) {
                    console.warn(`DrawSVG failed for ${strokeClass}, using fallback`)
                    gsap.to(element, {
                      strokeDashoffset: 0,
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: drawingEase
                    })
                  }
                } else {
                  gsap.to(element, {
                    strokeDashoffset: 0,
                    opacity: 1,
                    duration: duration,
                    delay: delay,
                    ease: drawingEase
                  })
                }
              }
            }

            // Draw F with sophisticated timing - 6x faster total
            drawLetter('stroke-f-1', 0, 0.4, 32) // Vertical stroke
            drawLetter('stroke-f-2', 0.3, 0.35, 35) // Top horizontal
            drawLetter('stroke-f-3', 0.55, 0.35, 38) // Middle horizontal

            // F Letter Particles - Motion Path Animation
            const fParticles = document.querySelectorAll('.particles-f .particle')
            fParticles.forEach((particle, index) => {
              const strokePaths = ['.stroke-f-1', '.stroke-f-2', '.stroke-f-3']
              const pathIndex = index % strokePaths.length
              const strokePath = document.querySelector(strokePaths[pathIndex])

              if (hasMotionPath && strokePath) {
                try {
                  gsap.to(particle, {
                    motionPath: {
                      path: strokePaths[pathIndex],
                      autoRotate: false,
                      alignOrigin: [0.5, 0.5]
                    },
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.6,
                    delay: index * 0.1,
                    ease: 'power2.out',
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                } catch (error) {
                  // Fallback: simple scale animation
                  gsap.to(particle, {
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.4,
                    delay: index * 0.1,
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                }
              }
            })
          }, 'drawF')

          // Reveal F text element
          .to(
            '.letter-f',
            {
              opacity: 1,
              scale: 1,
              duration: 0.2,
              ease: 'back.out(1.7)',
              onUpdate: function () {
                setProgress(Math.round(40 + this.progress() * 2))
              }
            },
            'drawF+=0.75'
          )

          // === A1 LETTER DRAWING ===
          .add(() => {
            const drawLetter = (
              strokeClass: string,
              delay: number,
              duration: number,
              progressStart: number
            ) => {
              const element = document.querySelector(`.${strokeClass}`)
              if (element) {
                if (hasDrawSVG) {
                  try {
                    gsap.to(element, {
                      drawSVG: '100%',
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: drawingEase,
                      onUpdate: function () {
                        setProgress(Math.round(progressStart + this.progress() * 6))
                      }
                    })
                  } catch (error) {
                    gsap.to(element, {
                      strokeDashoffset: 0,
                      opacity: 1,
                      duration,
                      delay,
                      ease: drawingEase
                    })
                  }
                } else {
                  gsap.to(element, {
                    strokeDashoffset: 0,
                    opacity: 1,
                    duration,
                    delay,
                    ease: drawingEase
                  })
                }
              }
            }

            drawLetter('stroke-a1-1', 0, 0.35, 42) // Left diagonal - 6x faster total
            drawLetter('stroke-a1-2', 0.25, 0.35, 44) // Right diagonal - 6x faster total
            drawLetter('stroke-a1-3', 0.45, 0.3, 46) // Cross bar - 6x faster total

            // A1 Letter Particles - Motion Path Animation
            const a1Particles = document.querySelectorAll('.particles-a1 .particle')
            a1Particles.forEach((particle, index) => {
              const strokePaths = ['.stroke-a1-1', '.stroke-a1-2', '.stroke-a1-3']
              const pathIndex = index % strokePaths.length

              if (hasMotionPath) {
                try {
                  gsap.to(particle, {
                    motionPath: {
                      path: strokePaths[pathIndex],
                      autoRotate: false,
                      alignOrigin: [0.5, 0.5]
                    },
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: 'power2.out',
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                } catch (error) {
                  gsap.to(particle, {
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.4,
                    delay: index * 0.08,
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                }
              }
            })
          }, 'drawA1')

          .to(
            '.letter-a1',
            { opacity: 1, scale: 1, duration: 0.2, ease: 'back.out(1.7)' },
            'drawA1+=0.65'
          )

          // === N LETTER DRAWING ===
          .add(() => {
            const drawLetter = (
              strokeClass: string,
              delay: number,
              duration: number,
              progressStart: number
            ) => {
              const element = document.querySelector(`.${strokeClass}`)
              if (element) {
                if (hasDrawSVG) {
                  try {
                    gsap.to(element, {
                      drawSVG: '100%',
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: drawingEase,
                      onUpdate: function () {
                        setProgress(Math.round(progressStart + this.progress() * 6))
                      }
                    })
                  } catch (error) {
                    gsap.to(element, {
                      strokeDashoffset: 0,
                      opacity: 1,
                      duration,
                      delay,
                      ease: drawingEase
                    })
                  }
                } else {
                  gsap.to(element, {
                    strokeDashoffset: 0,
                    opacity: 1,
                    duration,
                    delay,
                    ease: drawingEase
                  })
                }
              }
            }

            drawLetter('stroke-n-1', 0, 0.4, 48) // Left vertical - 6x faster total
            drawLetter('stroke-n-2', 0.25, 0.45, 50) // Diagonal - 6x faster total
            drawLetter('stroke-n-3', 0.5, 0.4, 52) // Right vertical - 6x faster total

            // N Letter Particles
            const nParticles = document.querySelectorAll('.particles-n .particle')
            nParticles.forEach((particle, index) => {
              const strokePaths = ['.stroke-n-1', '.stroke-n-2', '.stroke-n-3']
              const pathIndex = index % strokePaths.length
              if (hasMotionPath) {
                try {
                  gsap.to(particle, {
                    motionPath: {
                      path: strokePaths[pathIndex],
                      autoRotate: false,
                      alignOrigin: [0.5, 0.5]
                    },
                    scale: 1.2,
                    opacity: 0.8,
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: 'power2.out',
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                } catch (error) {
                  gsap.to(particle, {
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.4,
                    delay: index * 0.08,
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                }
              }
            })
          }, 'drawN')

          .to(
            '.letter-n',
            { opacity: 1, scale: 1, duration: 0.2, ease: 'back.out(1.7)' },
            'drawN+=0.7'
          )

          // === T LETTER DRAWING ===
          .add(() => {
            const drawLetter = (
              strokeClass: string,
              delay: number,
              duration: number,
              progressStart: number
            ) => {
              const element = document.querySelector(`.${strokeClass}`)
              if (element) {
                if (hasDrawSVG) {
                  try {
                    gsap.to(element, {
                      drawSVG: '100%',
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: drawingEase,
                      onUpdate: function () {
                        setProgress(Math.round(progressStart + this.progress() * 6))
                      }
                    })
                  } catch (error) {
                    gsap.to(element, {
                      strokeDashoffset: 0,
                      opacity: 1,
                      duration,
                      delay,
                      ease: drawingEase
                    })
                  }
                } else {
                  gsap.to(element, {
                    strokeDashoffset: 0,
                    opacity: 1,
                    duration,
                    delay,
                    ease: drawingEase
                  })
                }
              }
            }

            drawLetter('stroke-t-1', 0, 0.4, 54) // Top horizontal - 6x faster total
            drawLetter('stroke-t-2', 0.25, 0.45, 56) // Center vertical - 6x faster total

            // T Letter Particles
            const tParticles = document.querySelectorAll('.particles-t .particle')
            tParticles.forEach((particle, index) => {
              const strokePaths = ['.stroke-t-1', '.stroke-t-2']
              const pathIndex = index % strokePaths.length
              if (hasMotionPath) {
                try {
                  gsap.to(particle, {
                    motionPath: {
                      path: strokePaths[pathIndex],
                      autoRotate: false,
                      alignOrigin: [0.5, 0.5]
                    },
                    scale: 1.2,
                    opacity: 0.8,
                    duration: 0.5,
                    delay: index * 0.1,
                    ease: 'power2.out',
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                } catch (error) {
                  gsap.to(particle, {
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.4,
                    delay: index * 0.1,
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                }
              }
            })
          }, 'drawT')

          .to(
            '.letter-t',
            { opacity: 1, scale: 1, duration: 0.2, ease: 'back.out(1.7)' },
            'drawT+=0.6'
          )

          // === A2 LETTER DRAWING ===
          .add(() => {
            const drawLetter = (
              strokeClass: string,
              delay: number,
              duration: number,
              progressStart: number
            ) => {
              const element = document.querySelector(`.${strokeClass}`)
              if (element) {
                if (hasDrawSVG) {
                  try {
                    gsap.to(element, {
                      drawSVG: '100%',
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: drawingEase,
                      onUpdate: function () {
                        setProgress(Math.round(progressStart + this.progress() * 6))
                      }
                    })
                  } catch (error) {
                    gsap.to(element, {
                      strokeDashoffset: 0,
                      opacity: 1,
                      duration,
                      delay,
                      ease: drawingEase
                    })
                  }
                } else {
                  gsap.to(element, {
                    strokeDashoffset: 0,
                    opacity: 1,
                    duration,
                    delay,
                    ease: drawingEase
                  })
                }
              }
            }

            drawLetter('stroke-a2-1', 0, 0.35, 58) // Left diagonal - 6x faster total
            drawLetter('stroke-a2-2', 0.25, 0.35, 60) // Right diagonal - 6x faster total
            drawLetter('stroke-a2-3', 0.45, 0.3, 62) // Cross bar - 6x faster total

            // A2 Letter Particles
            const a2Particles = document.querySelectorAll('.particles-a2 .particle')
            a2Particles.forEach((particle, index) => {
              const strokePaths = ['.stroke-a2-1', '.stroke-a2-2', '.stroke-a2-3']
              const pathIndex = index % strokePaths.length
              if (hasMotionPath) {
                try {
                  gsap.to(particle, {
                    motionPath: {
                      path: strokePaths[pathIndex],
                      autoRotate: false,
                      alignOrigin: [0.5, 0.5]
                    },
                    scale: 1.2,
                    opacity: 0.8,
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: 'power2.out',
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                } catch (error) {
                  gsap.to(particle, {
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.4,
                    delay: index * 0.08,
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                }
              }
            })
          }, 'drawA2')

          .to(
            '.letter-a2',
            { opacity: 1, scale: 1, duration: 0.2, ease: 'back.out(1.7)' },
            'drawA2+=0.65'
          )

          // === X LETTER DRAWING - DRAMATIC CLIMAX ===
          .add(() => {
            const drawLetter = (
              strokeClass: string,
              delay: number,
              duration: number,
              progressStart: number
            ) => {
              const element = document.querySelector(`.${strokeClass}`)
              if (element) {
                if (hasDrawSVG) {
                  try {
                    gsap.to(element, {
                      drawSVG: '100%',
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: 'power2.inOut',
                      onUpdate: function () {
                        setProgress(Math.round(progressStart + this.progress() * 8))
                      }
                    })
                  } catch (error) {
                    gsap.to(element, {
                      strokeDashoffset: 0,
                      opacity: 1,
                      duration,
                      delay,
                      ease: 'power2.inOut'
                    })
                  }
                } else {
                  gsap.to(element, {
                    strokeDashoffset: 0,
                    opacity: 1,
                    duration,
                    delay,
                    ease: 'power2.inOut'
                  })
                }
              }
            }

            drawLetter('stroke-x-1', 0, 0.55, 64) // First diagonal - 6x faster total
            drawLetter('stroke-x-2', 0.35, 0.55, 68) // Second diagonal - 6x faster total

            // X Letter Particles
            const xParticles = document.querySelectorAll('.particles-x .particle')
            xParticles.forEach((particle, index) => {
              const strokePaths = ['.stroke-x-1', '.stroke-x-2']
              const pathIndex = index % strokePaths.length
              if (hasMotionPath) {
                try {
                  gsap.to(particle, {
                    motionPath: {
                      path: strokePaths[pathIndex],
                      autoRotate: false,
                      alignOrigin: [0.5, 0.5]
                    },
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: 'power2.out',
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                } catch (error) {
                  gsap.to(particle, {
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.4,
                    delay: index * 0.08,
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                }
              }
            })
          }, 'drawX')

          .to(
            '.letter-x',
            {
              opacity: 1,
              scale: 1.1,
              duration: 0.25,
              ease: 'elastic.out(1, 0.5)',
              onUpdate: function () {
                setProgress(Math.round(72 + this.progress() * 6))
              }
            },
            'drawX+=0.8'
          )

          // === I LETTER DRAWING - GRAND FINALE ===
          .add(() => {
            const drawLetter = (
              strokeClass: string,
              delay: number,
              duration: number,
              progressStart: number
            ) => {
              const element = document.querySelector(`.${strokeClass}`)
              if (element) {
                if (hasDrawSVG) {
                  try {
                    gsap.to(element, {
                      drawSVG: '100%',
                      opacity: 1,
                      duration: duration,
                      delay: delay,
                      ease: 'power2.inOut',
                      onUpdate: function () {
                        setProgress(Math.round(progressStart + this.progress() * 8))
                      }
                    })
                  } catch (error) {
                    gsap.to(element, {
                      strokeDashoffset: 0,
                      opacity: 1,
                      duration,
                      delay,
                      ease: 'power2.inOut'
                    })
                  }
                } else {
                  gsap.to(element, {
                    strokeDashoffset: 0,
                    opacity: 1,
                    duration,
                    delay,
                    ease: 'power2.inOut'
                  })
                }
              }
            }

            drawLetter('stroke-i-1', 0, 0.45, 78) // Top serif - 6x faster total
            drawLetter('stroke-i-2', 0.25, 0.6, 82) // Main vertical - 6x faster total
            drawLetter('stroke-i-3', 0.55, 0.45, 86) // Bottom serif - 6x faster total

            // I Letter Particles
            const iParticles = document.querySelectorAll('.particles-i .particle')
            iParticles.forEach((particle, index) => {
              const strokePaths = ['.stroke-i-1', '.stroke-i-2', '.stroke-i-3']
              const pathIndex = index % strokePaths.length
              if (hasMotionPath) {
                try {
                  gsap.to(particle, {
                    motionPath: {
                      path: strokePaths[pathIndex],
                      autoRotate: false,
                      alignOrigin: [0.5, 0.5]
                    },
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: 'power2.out',
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                } catch (error) {
                  gsap.to(particle, {
                    scale: 2.0,
                    opacity: 0.9,
                    duration: 0.4,
                    delay: index * 0.08,
                    onComplete: () => {
                      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.2 })
                    }
                  })
                }
              }
            })
          }, 'drawI')

          .to(
            '.letter-i',
            {
              opacity: 1,
              scale: 1.1,
              duration: 0.35,
              ease: 'elastic.out(1, 0.3)',
              onUpdate: function () {
                setProgress(Math.round(88 + this.progress() * 7))
              }
            },
            'drawI+=0.9'
          )

          // ===== PHASE 6: COMPLETION AND POLISH (85-100%) =====
          .to(
            '.loading-text',
            {
              opacity: 1,
              y: 0,
              ...(hasTextPlugin ? { text: 'Welcome to Fantasy XI!' } : {}),
              duration: 1.0,
              ease: 'power2.out',
              onUpdate: function () {
                setProgress(Math.round(95 + this.progress() * 3))
              }
            },
            '-=1.5'
          )

          // Final logo fills reveal
          .to(
            '.logo-fills',
            {
              opacity: 1,
              scale: 1,
              filter: 'brightness(1) saturate(1)',
              duration: 1.5,
              ease: 'power2.out',
              onUpdate: function () {
                setProgress(Math.round(98 + this.progress() * 2))
              }
            },
            '-=1.0'
          )

          // Hide all stroke paths
          .to(
            strokePaths,
            {
              opacity: 0,
              duration: 0.8,
              ease: 'power2.out'
            },
            '-=0.5'
          )

          // Final particle reset (particles handled per letter now)
          .add(() => {
            setProgress(100)
          }, '-=0.5')

          // ===== PHASE 7: SKELETON TRANSITION SEQUENCE (100-Exit) =====
          .to(
            '.progress-glow',
            {
              opacity: 0,
              scale: 0.8,
              duration: 0.4
            },
            '-=0.3'
          )

          // Hide progress and text elements
          .to(
            ['.loading-text', progressBarRef.current, '.progress-glow'],
            {
              opacity: 0,
              y: -20,
              duration: 0.3,
              ease: 'power2.in'
            },
            '-=0.2'
          )

          // Scale down the FANTAXI logo
          .to(
            logoRef.current,
            {
              scale: 0.3,
              opacity: 0.8,
              duration: 0.8,
              ease: 'power2.inOut'
            },
            '-=0.1'
          )

          // Create expanding circles overlay with full-screen skeleton
          .add(() => {
            // Create skeleton overlay structure that covers entire viewport
            const skeletonOverlay = document.createElement('div')
            skeletonOverlay.className = 'skeleton-overlay'
            skeletonOverlay.style.cssText = `
            position: fixed;
            inset: 0;
            z-index: 10002;
            background: hsl(220 13% 9%);
            overflow: hidden;
          `

            skeletonOverlay.innerHTML = `
            <!-- Skeleton Content Layout - Full Screen -->
            <div class="skeleton-content" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: hsl(220 13% 9%);">
              <div style="width: 100%; max-width: 64rem; padding: 1rem;">
                <div style="display: grid; gap: 1rem;">
                  <!-- Hero Skeleton -->
                  <div class="skeleton-element skeleton-hero" style="width: 100%; height: 8rem; background: hsl(215 28% 17%); border-radius: 0.5rem;"></div>
                  
                  <!-- Content Rows -->
                  <div class="skeleton-element skeleton-row" style="display: flex; gap: 1rem;">
                    <div class="skeleton-element skeleton-img" style="width: 6rem; height: 4rem; background: hsl(215 28% 17%); border-radius: 0.25rem;"></div>
                    <div style="flex: 1; display: flex; flex-direction: column; gap: 0.5rem;">
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 100%;"></div>
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 75%;"></div>
                    </div>
                  </div>
                  
                  <div class="skeleton-element skeleton-row" style="display: flex; gap: 1rem;">
                    <div class="skeleton-element skeleton-img" style="width: 6rem; height: 4rem; background: hsl(215 28% 17%); border-radius: 0.25rem;"></div>
                    <div style="flex: 1; display: flex; flex-direction: column; gap: 0.5rem;">
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 100%;"></div>
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 75%;"></div>
                    </div>
                  </div>
                  
                  <div class="skeleton-element skeleton-row" style="display: flex; gap: 1rem;">
                    <div class="skeleton-element skeleton-img" style="width: 6rem; height: 4rem; background: hsl(215 28% 17%); border-radius: 0.25rem;"></div>
                    <div style="flex: 1; display: flex; flex-direction: column; gap: 0.5rem;">
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 100%;"></div>
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 75%;"></div>
                    </div>
                  </div>
                  
                  <div class="skeleton-element skeleton-row" style="display: flex; gap: 1rem;">
                    <div class="skeleton-element skeleton-img" style="width: 6rem; height: 4rem; background: hsl(215 28% 17%); border-radius: 0.25rem;"></div>
                    <div style="flex: 1; display: flex; flex-direction: column; gap: 0.5rem;">
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 100%;"></div>
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 75%;"></div>
                    </div>
                  </div>
                  
                  <div class="skeleton-element skeleton-row" style="display: flex; gap: 1rem;">
                    <div class="skeleton-element skeleton-img" style="width: 6rem; height: 4rem; background: hsl(215 28% 17%); border-radius: 0.25rem;"></div>
                    <div style="flex: 1; display: flex; flex-direction: column; gap: 0.5rem;">
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 100%;"></div>
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 75%;"></div>
                    </div>
                  </div>
                  
                  <div class="skeleton-element skeleton-row" style="display: flex; gap: 1rem;">
                    <div class="skeleton-element skeleton-img" style="width: 6rem; height: 4rem; background: hsl(215 28% 17%); border-radius: 0.25rem;"></div>
                    <div style="flex: 1; display: flex; flex-direction: column; gap: 0.5rem;">
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 100%;"></div>
                      <div class="skeleton-element skeleton-text" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 75%;"></div>
                    </div>
                  </div>
                  
                  <!-- Text Block -->
                  <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 40%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 20%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 15%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 60%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 20%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 25%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 40%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 10%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 40%; display: inline-block;"></div>
                    <div class="skeleton-element skeleton-text-inline" style="height: 0.75rem; background: hsl(215 28% 17%); border-radius: 0.25rem; width: 15%; display: inline-block;"></div>
                  </div>
                  
                  <!-- Button -->
                  <div class="skeleton-element skeleton-button" style="width: 8rem; height: 2.5rem; background: rgba(59, 130, 246, 0.2); border-radius: 0.25rem;"></div>
                </div>
              </div>
            </div>
            
            <!-- Expanding Circles SVG -->
            <svg class="circles-loader" viewBox="0 0 100 100" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 120px; height: 120px; z-index: 10003; overflow: visible;">
              <g class="core-circle">
                <circle class="path" cx="50" cy="50" r="1" fill="none" />
              </g>
              <g class="spinner-circle">
                <circle class="path" cx="50" cy="50" r="20" fill="none" />
              </g>
              <g class="layer-1-circle"><circle class="path" cx="50" cy="50" r="7" fill="none" /></g>
              <g class="layer-2-circle"><circle class="path" cx="50" cy="50" r="12" fill="none" /></g>
              <g class="layer-3-circle"><circle class="path" cx="50" cy="50" r="18" fill="none" /></g>
              <g class="layer-4-circle"><circle class="path" cx="50" cy="50" r="24" fill="none" /></g>
              <g class="layer-5-circle"><circle class="path" cx="50" cy="50" r="30" fill="none" /></g>
              <g class="layer-6-circle"><circle class="path" cx="50" cy="50" r="38" fill="none" /></g>
              <g class="layer-7-circle"><circle class="path" cx="50" cy="50" r="45" fill="none" /></g>
              <g class="layer-8-circle"><circle class="path" cx="50" cy="50" r="54" fill="none" /></g>
            </svg>
          `

            if (loaderRef.current) {
              loaderRef.current.appendChild(skeletonOverlay)
            }

            // Setup circles animation similar to PageTransition
            const brandPrimary = 'hsl(var(--primary))'
            const brandAccent = 'hsl(var(--accent))'
            const brandDark = 'hsl(var(--secondary))'

            const layers = [
              { radius: 70, width: 15, color: `rgba(59, 130, 246, 0.5)`, rotation: -30 },
              { radius: 120, width: 20, color: `rgba(96, 165, 250, 0.3)`, rotation: -60 },
              { radius: 180, width: 40, color: `rgba(59, 130, 246, 0.8)`, rotation: -70 },
              { radius: 240, width: 20, color: `rgba(30, 41, 59, 0.6)`, rotation: -90 },
              { radius: 300, width: 30, color: `rgba(59, 130, 246, 0.5)`, rotation: -20 },
              { radius: 380, width: 45, color: `rgba(96, 165, 250, 0.2)`, rotation: -80 },
              { radius: 450, width: 75, color: `rgba(59, 130, 246, 1)`, rotation: -10 },
              { radius: 540, width: 250, color: `rgba(59, 130, 246, 0.5)`, rotation: -70 }
            ]

            // Setup skeleton elements
            gsap.set('.skeleton-element', {
              scale: 0,
              transformOrigin: '50% 50%'
            })

            gsap.set('.skeleton-text, .skeleton-text-inline', {
              scaleX: 0,
              transformOrigin: '0 50%'
            })

            // Setup circles
            layers.forEach((layer, index) => {
              const circumference = 2 * Math.PI * layer.radius
              gsap.set(`.layer-${index + 1}-circle .path`, {
                stroke: layer.color,
                strokeWidth: layer.width,
                strokeDasharray: circumference,
                strokeDashoffset: circumference,
                rotation: layer.rotation,
                transformOrigin: '50% 50%',
                visibility: 'hidden'
              })
            })

            const spinnerCircumference = 2 * Math.PI * 20
            gsap.set('.spinner-circle .path', {
              stroke: brandPrimary,
              strokeWidth: 6,
              strokeDasharray: spinnerCircumference,
              strokeDashoffset: spinnerCircumference,
              transformOrigin: '50% 50%'
            })

            gsap.set('.core-circle .path', {
              fill: brandPrimary,
              scale: 1,
              opacity: 0,
              transformOrigin: '50% 50%'
            })
          })

          // Start circles expansion
          .to('.spinner-circle', {
            rotation: 270,
            duration: 1.25,
            ease: 'linear'
          })

          .to(
            '.spinner-circle .path',
            {
              strokeDashoffset: 125.66 / 4, // circumference / 4
              rotation: 135,
              duration: 1.25,
              ease: 'ease-in-out'
            },
            '<'
          )

          // Sequential layer expansion
          .add(() => {
            const layers = [
              { radius: 70, width: 15, color: `rgba(112, 196, 147, 0.5)`, rotation: -30 },
              { radius: 120, width: 20, color: `rgba(111, 204, 221, 0.3)`, rotation: -60 },
              { radius: 180, width: 40, color: `rgba(112, 196, 147, 0.8)`, rotation: -70 },
              { radius: 240, width: 20, color: `rgba(58, 68, 84, 0.6)`, rotation: -90 },
              { radius: 300, width: 30, color: `rgba(112, 196, 147, 0.5)`, rotation: -20 },
              { radius: 380, width: 45, color: `rgba(111, 204, 221, 0.2)`, rotation: -80 },
              { radius: 450, width: 75, color: `rgba(112, 196, 147, 1)`, rotation: -10 },
              { radius: 540, width: 250, color: `rgba(112, 196, 147, 0.5)`, rotation: -70 }
            ]

            layers.forEach((layer, index) => {
              const circumference = 2 * Math.PI * layer.radius
              const delay = index * 0.1

              gsap.set(`.layer-${index + 1}-circle .path`, {
                visibility: 'visible',
                delay: delay
              })

              gsap.to(`.layer-${index + 1}-circle .path`, {
                strokeDashoffset: circumference / 2,
                duration: 0.75,
                ease: 'ease-in',
                delay: delay
              })

              gsap.to(`.layer-${index + 1}-circle .path`, {
                stroke: layer.color.replace(/[\d\.]+\)$/, '1)'),
                strokeWidth: layer.width * (index === 0 ? 15 : 3),
                strokeDashoffset: 0,
                rotation: layer.rotation + 480,
                duration: 0.5,
                ease: 'ease-out',
                delay: delay + 0.75
              })
            })
          }, '<0.3')

          // Core explosion
          .to(
            '.core-circle .path',
            {
              scale: 1000,
              opacity: 1,
              duration: 1.6,
              ease: 'ease-in-out'
            },
            '-=1.5'
          )

          .to(
            '.core-circle .path',
            {
              opacity: 0,
              duration: 0.8,
              ease: 'ease-in-out'
            },
            '-=0.5'
          )

          // Scale down circles
          .to(
            '.circles-loader',
            {
              scale: 0,
              duration: 1,
              ease: 'ease-in-out'
            },
            '-=0.3'
          )

          // Animate skeleton elements in
          .to(
            '.skeleton-img, .skeleton-hero, .skeleton-button',
            {
              scale: 1,
              duration: 0.3,
              ease: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
              stagger: 0.1
            },
            '-=0.5'
          )

          .to(
            '.skeleton-text, .skeleton-text-inline',
            {
              scaleX: 1,
              duration: 0.1,
              ease: 'ease-out',
              stagger: 0.05
            },
            '-=0.3'
          )

          // Show skeleton briefly, then start reverse circle animation
          .add(() => {}, '+=0.8')

          // REVERSE CIRCLE ANIMATION - Contract circles from outside to center
          .add(() => {
            const layers = [
              { radius: 70, width: 15, color: `rgba(59, 130, 246, 0.5)`, rotation: -30 },
              { radius: 120, width: 20, color: `rgba(96, 165, 250, 0.3)`, rotation: -60 },
              { radius: 180, width: 40, color: `rgba(59, 130, 246, 0.8)`, rotation: -70 },
              { radius: 240, width: 20, color: `rgba(30, 41, 59, 0.6)`, rotation: -90 },
              { radius: 300, width: 30, color: `rgba(59, 130, 246, 0.5)`, rotation: -20 },
              { radius: 380, width: 45, color: `rgba(96, 165, 250, 0.2)`, rotation: -80 },
              { radius: 450, width: 75, color: `rgba(59, 130, 246, 1)`, rotation: -10 },
              { radius: 540, width: 250, color: `rgba(59, 130, 246, 0.5)`, rotation: -70 }
            ]

            // Reverse order - start from outermost circles
            layers.reverse().forEach((layer, index) => {
              const circumference = 2 * Math.PI * layer.radius
              const delay = index * 0.08
              const layerIndex = 8 - index // Get original layer index

              // Contract the circles (reverse the expansion)
              gsap.to(`.layer-${layerIndex}-circle .path`, {
                strokeDashoffset: circumference,
                strokeWidth: layer.width,
                rotation: layer.rotation,
                duration: 0.6,
                ease: 'power2.out',
                delay: delay
              })

              // Fade out the circle stroke
              gsap.to(`.layer-${layerIndex}-circle .path`, {
                stroke: layer.color.replace(/[\d\.]+\)$/, '0)'),
                duration: 0.4,
                ease: 'power2.out',
                delay: delay + 0.3
              })
            })
          })

          // Core implosion - reverse the explosion
          .to(
            '.core-circle .path',
            {
              scale: 1,
              opacity: 1,
              duration: 0.8,
              ease: 'power2.inOut'
            },
            '<'
          )

          .to(
            '.core-circle .path',
            {
              scale: 0,
              opacity: 0,
              duration: 0.6,
              ease: 'power2.in'
            },
            '<0.4'
          )

          // Reverse spinner animation
          .to(
            '.spinner-circle',
            {
              rotation: 0,
              duration: 0.8,
              ease: 'power2.inOut'
            },
            '<'
          )

          .to(
            '.spinner-circle .path',
            {
              strokeDashoffset: 125.66, // Back to full circumference
              rotation: 0,
              duration: 0.8,
              ease: 'power2.inOut'
            },
            '<'
          )

          // Scale up circles loader to reveal page underneath
          .to(
            '.circles-loader',
            {
              scale: 50,
              duration: 1.2,
              ease: 'power2.inOut'
            },
            '<0.3'
          )

          // Fade skeleton content as circles contract
          .to(
            '.skeleton-content',
            {
              opacity: 0,
              scale: 0.95,
              duration: 0.8,
              ease: 'power2.out'
            },
            '<0.2'
          )

          // Complete transition - remove overlay
          .add(() => {
            // Clean up skeleton overlay
            const overlay = document.querySelector('.skeleton-overlay')
            if (overlay) {
              overlay.remove()
            }
            // Complete the loading process
            handleComplete()
          }, '+=0.3')

          // Scale down original loader
          .to(
            loaderRef.current,
            {
              scale: 0,
              duration: 0.5,
              ease: 'power2.in'
            },
            '<-0.5'
          )
      } catch (error) {
        console.error('BrandedLoader: Animation setup failed:', error)
        // Fallback: immediate completion with basic fade
        gsap.to('.loader-content', {
          opacity: 1,
          duration: 0.5,
          onComplete: handleComplete
        })
      }
    },
    { scope: loaderRef, dependencies: [isHydrated, handleComplete] }
  )

  // Update progress bar width
  useEffect(() => {
    if (progressFillRef.current) {
      gsap.to(progressFillRef.current, {
        width: `${progress}%`,
        duration: 0.3,
        ease: 'power2.out'
      })
    }
  }, [progress])

  // Simple fallback for SSR to prevent hydration mismatch
  if (!isHydrated) {
    return (
      <div
        className={`fixed inset-0 z-[9999] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center ${className}`}
      >
        <div className="text-center">
          <div className="text-primary text-xl font-bold mb-4">{loadingText}</div>
          <div className="flex justify-center space-x-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 bg-primary rounded-full animate-pulse"
                style={{
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '1s'
                }}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={loaderRef}
      className={`fixed inset-0 z-[9999] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center ${className}`}
    >
      <div
        className="loader-content text-center"
        style={{ opacity: 0, transform: 'translateY(50px) rotateX(-15deg) scale(0.95)' }}
      >
        {/* Brand Logo Image (static) */}
        <div className="relative w-40 h-40 mb-8 mx-auto">
          <img
            ref={logoRef as any}
            src="/assets/Logos/NPFCwhiteLogo.png"
            alt="Brand Logo"
            className="w-full h-full object-contain"
          />
        </div>

        {/* Loading text with typewriter effect */}
        <div
          className="loading-text text-xl mb-6 font-medium tracking-wide"
          style={{ color: LOADER_PRIMARY, opacity: 0, transform: 'translateY(20px)' }}
        >
          {loadingText}
        </div>

        {/* Gaming-style progress bar */}
        <div
          className="relative w-80 h-3 bg-slate-700/50 rounded-full overflow-hidden border mb-4"
          style={{ borderColor: LOADER_PRIMARY }}
        >
          {/* Progress glow effect */}
          <div
            className="progress-glow absolute inset-0 blur-sm"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(60,131,246,0.5), transparent)',
              opacity: 0,
              transform: 'scale(0.8)'
            }}
          />

          {/* Progress fill */}
          <div
            ref={progressFillRef}
            className="relative h-full transition-all duration-300 ease-out"
            style={{
              width: '0%',
              background: `linear-gradient(90deg, ${LOADER_PRIMARY}, ${LOADER_PRIMARY}, ${LOADER_PRIMARY})`
            }}
          />

          {/* Progress highlight */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)'
            }}
          />
        </div>

        {/* Progress percentage */}
        <div
          className="text-lg font-bold tabular-nums tracking-wider"
          style={{ color: LOADER_PRIMARY }}
        >
          {progress}%
        </div>

        {/* Subtle loading dots */}
        <div className="flex justify-center space-x-2 mt-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full animate-pulse"
              style={{
                backgroundColor: LOADER_PRIMARY,
                animationDelay: `${i * 0.2}s`,
                animationDuration: '1s'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
